/*
 * ---------------------------------------------------------------------------------
 * Copyright:
 *      NewtonGreen Technologies Pty. Ltd.
 *      Level 4, 175 Scott St.
 *      Newcastle, NSW, 2300
 *      Australia
 *
 *      E-mail: support@newtongreen.com
 *      Tel: (02) 4925 5288
 *      Fax: (02) 4925 3068
 *
 *      All Rights Reserved.
 * ---------------------------------------------------------------------------------
 */

/*
 * --------------------------------------------------------------------------------
 * This file contains the reports landing page component
 * --------------------------------------------------------------------------------
 */

/*
 * ---------------------------------------------------------------------------------
 * Imports - External
 * ---------------------------------------------------------------------------------
 */

/**
 * Required to make use of JSX functionality
 */
import { Box, Grid, makeStyles, Paper, Theme } from '@material-ui/core';
import { InstitutionsContext, InstitutionSelector } from '@ngt/online-patient-management-ui';
import { RequestState } from '@ngt/request-utilities';
import * as React from 'react';
import { useHistory, useParams } from 'react-router';
import { Column, Row } from 'react-table';
import { createReportUrl, IReportParam, ReportExportFormat } from '../../utilities/createReportUrl';
import ReportTable, { IReportItem } from './ReportTable';
import * as Dtos from '../../api/Neon.Dtos';
import PageLayout from 'src/components/Layout/PageLayout';
import { useBctApiPermissions } from '@ngt/online-patient-management-bctapi-permissions';

/*
 * ---------------------------------------------------------------------------------
 * Interfaces
 * ---------------------------------------------------------------------------------
 */

interface IReportsProps {
}

interface IReportParams {
    institutionCode?: string;
}

/*
 * ---------------------------------------------------------------------------------
 * Constants
 * ---------------------------------------------------------------------------------
 */
const reportPermissions = [Dtos.Permission.OpmsAdminister];

/*
 * ---------------------------------------------------------------------------------
 * Styles
 * ---------------------------------------------------------------------------------
 */

const useStyles = makeStyles<Theme>(theme => ({
    container: {
        padding: theme.spacing(0)
    },
    buttonSet: {
        textAlign: 'right'
    },
    button: {
        marginLeft: theme.spacing(3),

        '&:first-child': {
            marginLeft: theme.spacing(0)
        }
    },
    marginTop: {
        marginTop: theme.spacing(2)
    },
    padding: {
        padding: theme.spacing(3)
    }
}));


const useReportColumns = (_reportItems: IReportItem[]) => {
    const columns = React.useMemo(() => {
        const cols: Array<Column<IReportItem>> = [
            {
                Header: 'Report',
                accessor: 'reportTitle',
                minWidth: 155,
                maxWidth: 155
            },
            {
                Header: 'Access',
                accessor: 'access',
            }
        ];

        return cols;
    }, []);

    return columns;
};


const Reports: React.FunctionComponent<IReportsProps> = () => {
    const classes = useStyles();
    const history = useHistory();

    const [[canAdministerOpms], reportPermissionLoadState] = useBctApiPermissions(reportPermissions);
    
    const [institutions, institutionsLoadState,] = React.useContext(InstitutionsContext);

    const institutionsToUse = React.useMemo(() => {
        return institutions?.filter(i => (i as Dtos.Institution).activatedDate != null) ?? []
    }, [institutions]);

    const params = useParams<IReportParams>();

    const institutionsLoading = institutionsLoadState.state === RequestState.None || institutionsLoadState.state === RequestState.Pending;

    const selectedInstCode = params.institutionCode;

    const userKeyParam: IReportParam[] = React.useMemo(() => {
        return [
            { paramName: 'UserKey', paramValue: 'EmailLink' }
        ];
    }, []);

    const defaultParams = React.useMemo(() => {
        const reportParams: IReportParam[] = [
            ...userKeyParam
        ];

        if (selectedInstCode) {
            reportParams.push({ paramName: 'InstCode', paramValue: selectedInstCode } as IReportParam);
        }

        return reportParams;
    }, [selectedInstCode, userKeyParam]);

    const enrollmentStatusParams = React.useMemo(() => {
        const reportParams: IReportParam[] = [
            ...userKeyParam
        ];

        if (selectedInstCode) {
            reportParams.push({ paramName: 'InstitutionCode', paramValue: selectedInstCode } as IReportParam);
        }

        return reportParams;
    }, [selectedInstCode, userKeyParam]);

    const reports: IReportItem[] = [
        {
            reportTitle: 'Depot Stock',
            reportFile: 'DepotStock',
            reportParams: userKeyParam,
            access: "BCT Staff"
        },
        {
            reportTitle: 'Drug Shipment',
            reportFile: 'DrugShipmentReport',
            reportParams: defaultParams,
            access: "BCT Staff"
        },
        {
            reportTitle: 'Patient Action',
            reportFile: 'PatientActionReport',
            reportParams: defaultParams,
            access: "BCT Staff"
        },
        {
            reportTitle: 'Drug Management Log',
            reportFile: 'DrugManagementLog',
            reportParams: userKeyParam,
            access: "BCT Staff"
        },
        {
            reportTitle: 'Depot Stock Arms A and B',
            reportFile: 'DepotStockArmAB',
            reportParams: userKeyParam,
            access: "BCT Staff"
        },
        {
            reportTitle: 'Drug Shipment Arms A and B',
            reportFile: 'DrugShipmentReportArmAB',
            reportParams: defaultParams,
            access: "BCT Staff"
        },
        {
            reportTitle: 'Patient Action Arms A and B',
            reportFile: 'PatientActionReportArmAB',
            reportParams: defaultParams,
            access: "BCT Staff"
        },
        {
            reportTitle: 'Pharmacy Batches Arms A and B',
            reportFile: 'PharmacyBatchesReportArmAB',
            reportParams: userKeyParam,
            access: "BCT Staff"
        },
        {
            reportTitle: 'Site Status',
            reportFile: 'SiteStatusReport',
            reportFormat: ReportExportFormat.Word,
            reportParams: [
                { paramName: 'Token', paramValue: 'EmailLink' }
            ],
            access: "BCT Staff"
        },
        {
            reportTitle: 'Enrollment Status',
            reportFile: 'EnrolmentStatusReport',
            reportParams: enrollmentStatusParams,
            access: "BCT Staff"
        }
    ];

    const reportColumns = useReportColumns(reports);

    const onReportClick = React.useCallback((row: Row<IReportItem>) => {
        if (row &&
            row.original) {
            const reportUrl: string = createReportUrl(row.original.reportFile,
                row.original.reportParams,
                row.original.reportFormat ? row.original.reportFormat as unknown as ReportExportFormat : undefined,
                true);

            window.open(reportUrl);
        }
    }, []);

    React.useEffect(() => {
        if (reportPermissionLoadState.state !== RequestState.Pending &&
            reportPermissionLoadState.state !== RequestState.None) {
            if (!canAdministerOpms) {
                history.replace(`/error/403`);
            }
        }
    }, [canAdministerOpms, reportPermissionLoadState, history]);

    return (
        <Box
            className={classes.marginTop}
        >
            <PageLayout
                heading="Reports"
            >
                <Paper
                    className={classes.container}
                >
                    <Box
                        className={classes.padding}
                    >
                        <Grid container alignItems="flex-end" spacing={2}>
                            <Grid item xs={12}>
                                <InstitutionSelector
                                    allowAllInstitutions={true}
                                    baseRoute="/reports"
                                    institutionOnlyRoute="/reports/:institutionCode"
                                    institutions={institutionsToUse}
                                    loading={institutionsLoading}
                                    showCountry={false}
                                    showTreatment={false}
                                    institutionCode={selectedInstCode}
                                    countryOnlyRoute="/reports"
                                    countryAndInstitutionOnlyRoute="/reports/:institutionCode"
                                />
                            </Grid>
                        </Grid>
                    </Box>
                    <ReportTable
                        data={reports}
                        columns={reportColumns}
                        onRowClick={onReportClick}
                    />
                </Paper>
            </PageLayout>
        </Box>
    );
}

export default Reports;